/**
 * Minouko — comportements de la boutique.
 * JavaScript natif, aucun framework, aucune dépendance.
 * Tout ce qui est ici dégrade proprement : sans JS, les formulaires postent
 * normalement et les liens fonctionnent.
 */
(() => {
  'use strict';

  const euro = (cents) =>
    (cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) +
    ' €';

  /** Le CDN Shopify redimensionne via ?width=, pas via le suffixe _200x hérité. */
  const sized = (url, w) => {
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('width', String(w));
      return u.toString();
    } catch (_) {
      return url;
    }
  };

  /**
   * Les API Shopify n'expriment pas les prix de la même façon : l'API panier et
   * celle des recommandations renvoient des centiemes entiers, celle de
   * suggestion une chaîne décimale. Sans normalisation, un prix sort cent fois
   * trop élevé.
   */
  const toCents = (v) => {
    if (typeof v === 'number') return Math.round(v);
    const str = String(v).trim();
    if (/[.,]/.test(str)) return Math.round(parseFloat(str.replace(',', '.')) * 100);
    return Math.round(Number(str));
  };

  const focusable =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  /* ───────────────────────── Tiroir panier ───────────────────────── */

  const Drawer = {
    el: null,
    panel: null,
    body: null,
    foot: null,
    lastFocus: null,

    init() {
      this.el = document.getElementById('cart-drawer');
      if (!this.el) return;
      this.panel = this.el.querySelector('.drawer__panel');
      this.body = this.el.querySelector('[data-drawer-body]');
      this.foot = this.el.querySelector('[data-drawer-foot]');

      this.el.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]') || e.target === this.el.querySelector('.drawer__veil')) {
          this.close();
        }
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
        if (e.key === 'Tab' && this.isOpen()) this.trap(e);
      });

      document.querySelectorAll('[data-cart-open]').forEach((b) =>
        b.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        })
      );

      this.body.addEventListener('click', (e) => {
        const step = e.target.closest('[data-step]');
        if (step) {
          e.preventDefault();
          const line = Number(step.dataset.line);
          const qty = Number(step.dataset.qty);
          this.change(line, qty);
        }
      });
    },

    isOpen() {
      return this.el && this.el.hasAttribute('open');
    },

    trap(e) {
      const nodes = [...this.panel.querySelectorAll(focusable)].filter((n) => n.offsetParent !== null);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },

    async open() {
      if (!this.el) return;
      this.lastFocus = document.activeElement;
      this.el.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';
      await this.refresh();
      const target = this.panel.querySelector('[data-drawer-close]');
      if (target) target.focus();
    },

    close() {
      if (!this.el) return;
      this.el.removeAttribute('open');
      document.documentElement.style.overflow = '';
      if (this.lastFocus) this.lastFocus.focus();
    },

    async refresh() {
      try {
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        this.render(cart);
        Drawer.setCount(cart.item_count);
      } catch (_) {
        this.body.innerHTML = '<p class="drawer__msg">Le panier est momentanément indisponible. Rechargez la page.</p>';
      }
    },

    setCount(n) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = n > 0 ? n : '';
        el.hidden = n === 0;
      });
    },

    render(cart) {
      if (!cart.items.length) {
        this.body.innerHTML =
          '<div class="drawer__empty"><p>Votre panier est vide.</p>' +
          '<p><a href="/collections">Parcourir les rayons</a></p></div>';
        this.foot.hidden = true;
        return;
      }
      this.foot.hidden = false;
      this.body.innerHTML =
        '<ul class="dlines">' +
        cart.items
          .map((it, i) => {
            const line = i + 1;
            const img = it.image ? `<img src="${sized(it.image, 200)}" alt="" decoding="async">` : '';
            const variant =
              it.options_with_values && it.options_with_values.length > 1
                ? `<p class="dline__v">${it.options_with_values
                    .filter((o) => !/ships?\s*from|origin/i.test(o.name))
                    .map((o) => o.value)
                    .join(' · ')}</p>`
                : '';
            return `<li class="dline">
              <a class="dline__ph" href="${it.url}" tabindex="-1" aria-hidden="true">${img}</a>
              <div class="dline__body">
                <h3 class="dline__t"><a href="${it.url}">${it.product_title}</a></h3>
                ${variant}
                <div class="stepper" role="group" aria-label="Quantité">
                  <button type="button" data-step data-line="${line}" data-qty="${it.quantity - 1}" aria-label="Retirer un exemplaire">&minus;</button>
                  <span class="num" aria-live="polite">${it.quantity}</span>
                  <button type="button" data-step data-line="${line}" data-qty="${it.quantity + 1}" aria-label="Ajouter un exemplaire">+</button>
                </div>
              </div>
              <div class="dline__end">
                <span class="num">${euro(it.final_line_price)}</span>
                <button type="button" class="dline__rm" data-step data-line="${line}" data-qty="0">Retirer</button>
              </div>
            </li>`;
          })
          .join('') +
        '</ul>';
      this.foot.querySelector('[data-drawer-total]').textContent = euro(cart.total_price);
      this.crossSell(cart);
    },

    /**
     * Vente croisée. On part du dernier article ajouté et on propose ses
     * voisins de rayon, en excluant ce qui est déjà au panier. Rien ne
     * s'affiche si Shopify ne renvoie rien : pas de bloc vide.
     */
    async crossSell(cart) {
      const host = this.el.querySelector('[data-drawer-cross]');
      if (!host || !cart.items.length) return;
      const seed = cart.items[cart.items.length - 1];
      if (host.dataset.seed === String(seed.product_id)) return;
      host.dataset.seed = String(seed.product_id);
      const inCart = new Set(cart.items.map((i) => i.product_id));
      try {
        const d = await fetch(
          `/recommendations/products.json?product_id=${seed.product_id}&limit=6&intent=related`,
          { headers: { Accept: 'application/json' } }
        ).then((r) => r.json());
        const picks = (d.products || []).filter((p) => !inCart.has(p.id)).slice(0, 3);
        if (!picks.length) {
          host.innerHTML = '';
          return;
        }
        host.innerHTML =
          '<h3 class="cross__h">À compléter</h3><ul class="cross">' +
          picks
            .map((p) => {
              const src =
                (p.featured_image && (p.featured_image.url || p.featured_image)) ||
                (p.images && p.images[0]) ||
                null;
              const img = src ? `<img src="${sized(src, 120)}" alt="" decoding="async">` : '';
              return `<li><a href="${p.url}">
                <span class="cross__ph">${img}</span>
                <span class="cross__t">${p.title}</span>
                <span class="price num">${euro(toCents(p.price))}</span>
              </a></li>`;
            })
            .join('') +
          '</ul>';
      } catch (_) {
        host.innerHTML = '';
      }
    },

    async change(line, quantity) {
      this.body.setAttribute('aria-busy', 'true');
      try {
        const cart = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity }),
        }).then((r) => r.json());
        this.render(cart);
        Drawer.setCount(cart.item_count);
      } finally {
        this.body.removeAttribute('aria-busy');
      }
    },
  };

  /* ─────────────── Ajout au panier, depuis n'importe où ─────────────── */

  async function addToCart(id, quantity, button) {
    const label = button ? button.innerHTML : null;
    if (button) {
      button.disabled = true;
      button.dataset.busy = 'true';
    }
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: quantity || 1 }] }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Ajout impossible.');
      }
      await Drawer.open();
    } catch (e) {
      if (button) {
        button.dataset.error = 'true';
        button.innerHTML = e.message.slice(0, 60);
        setTimeout(() => {
          delete button.dataset.error;
          button.innerHTML = label;
        }, 3200);
      }
    } finally {
      if (button) {
        button.disabled = false;
        delete button.dataset.busy;
      }
    }
  }

  document.addEventListener('click', (e) => {
    const quick = e.target.closest('[data-quick-add]');
    if (quick) {
      e.preventDefault();
      addToCart(quick.dataset.quickAdd, 1, quick);
    }
  });

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const id = form.querySelector('[name="id"]').value;
    const qty = form.querySelector('[name="quantity"]');
    addToCart(id, qty ? Number(qty.value) : 1, form.querySelector('[type="submit"]'));
  });

  /* ───────────────────────── Galerie produit ───────────────────────── */

  function gallery() {
    const root = document.querySelector('[data-gallery]');
    if (!root) return;
    const main = root.querySelector('[data-gallery-main]');
    const thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
    if (!main || !thumbs.length) return;

    const show = (btn) => {
      const full = btn.dataset.full;
      if (!full) return;
      main.src = full;
      main.srcset = '';
      main.alt = btn.dataset.alt || '';
      thumbs.forEach((t) => t.setAttribute('aria-current', t === btn ? 'true' : 'false'));
    };

    thumbs.forEach((btn) => {
      btn.addEventListener('click', () => show(btn));
      btn.addEventListener('keydown', (e) => {
        const i = thumbs.indexOf(btn);
        if (e.key === 'ArrowRight' && thumbs[i + 1]) {
          e.preventDefault();
          thumbs[i + 1].focus();
          show(thumbs[i + 1]);
        }
        if (e.key === 'ArrowLeft' && thumbs[i - 1]) {
          e.preventDefault();
          thumbs[i - 1].focus();
          show(thumbs[i - 1]);
        }
      });
    });
  }

  /* ─────────────────────── Sélection de variante ─────────────────────── */

  function variants() {
    const form = document.querySelector('[data-product-form]');
    if (!form) return;
    const data = document.getElementById('variant-data');
    if (!data) return;

    let list;
    try {
      list = JSON.parse(data.textContent);
    } catch (_) {
      return;
    }

    const idField = form.querySelector('[name="id"]');
    const priceWrap = document.querySelector('[data-variant-price-wrap]');
    const priceEl = priceWrap ? priceWrap.querySelector('.num') : null;
    const compareEl = priceWrap ? priceWrap.querySelector('.price__was') : null;
    const stickyPrice = document.querySelector('[data-sticky-price]');
    const submit = form.querySelector('[type="submit"]');
    const submitPrice = submit ? submit.querySelector('[data-submit-price]') : null;

    const apply = () => {
      const chosen = [...form.querySelectorAll('input[type="radio"]:checked')].reduce((acc, input) => {
        acc[Number(input.dataset.optionIndex)] = input.value;
        return acc;
      }, {});
      const match = list.find((v) =>
        Object.entries(chosen).every(([i, val]) => v.options[Number(i)] === val)
      );
      if (!match) return;
      idField.value = match.id;
      if (priceEl) priceEl.textContent = euro(match.price);
      if (compareEl) {
        const has = match.compare && match.compare > match.price;
        compareEl.hidden = !has;
        if (has) compareEl.textContent = euro(match.compare);
      }
      if (submitPrice) submitPrice.textContent = euro(match.price);
      if (stickyPrice) stickyPrice.textContent = euro(match.price);
      if (submit) {
        submit.disabled = !match.available;
        const label = submit.querySelector('[data-submit-label]');
        if (label) label.textContent = match.available ? 'Ajouter au panier' : 'Épuisé';
      }
      const stickyBtn = document.querySelector('[data-sticky-add]');
      if (stickyBtn) {
        stickyBtn.disabled = !match.available;
        stickyBtn.textContent = match.available ? 'Ajouter' : 'Épuisé';
      }
      const url = new URL(window.location);
      url.searchParams.set('variant', match.id);
      history.replaceState({}, '', url);
    };

    form.addEventListener('change', (e) => {
      if (e.target.matches('input[type="radio"]')) apply();
    });
  }

  /* ───────────────────── Description dépliable ───────────────────── */

  function expanders() {
    document.querySelectorAll('[data-expand]').forEach((btn) => {
      const target = document.getElementById(btn.getAttribute('aria-controls'));
      if (!target) return;
      // Rien à déplier si le texte tient déjà.
      if (target.scrollHeight <= target.clientHeight + 4) {
        btn.hidden = true;
        target.classList.remove('is-clamped');
        return;
      }
      btn.addEventListener('click', () => {
        const open = target.classList.toggle('is-open');
        target.classList.toggle('is-clamped', !open);
        btn.setAttribute('aria-expanded', String(open));
        btn.textContent = open ? 'Réduire' : btn.dataset.expand;
      });
    });
  }


  /* ───────────────── Recherche en surimpression ─────────────────
     Résultats à la frappe via l'API de suggestion de Shopify. La
     recherche complète reste accessible sans JS par le formulaire. */

  function search() {
    const ovl = document.getElementById('search-overlay');
    if (!ovl) return;
    const input = ovl.querySelector('[data-search-input]');
    const list = ovl.querySelector('[data-search-list]');
    const all = ovl.querySelector('[data-search-all]');
    let lastFocus = null;
    let timer = null;
    let controller = null;

    const open = () => {
      lastFocus = document.activeElement;
      ovl.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';
      input.focus();
      input.select();
    };
    const close = () => {
      ovl.removeAttribute('open');
      document.documentElement.style.overflow = '';
      if (lastFocus) lastFocus.focus();
    };

    document.querySelectorAll('[data-search-open]').forEach((b) =>
      b.addEventListener('click', (e) => {
        e.preventDefault();
        open();
      })
    );
    ovl.addEventListener('click', (e) => {
      if (e.target.closest('[data-search-close]') || e.target.classList.contains('ovl__veil')) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && ovl.hasAttribute('open')) close();
      // Barre oblique pour ouvrir, comme partout ailleurs.
      if (e.key === '/' && !/^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement.tagName)) {
        e.preventDefault();
        open();
      }
    });

    const render = (products, term) => {
      all.href = '/search?q=' + encodeURIComponent(term);
      all.hidden = !term;
      if (!products.length) {
        list.innerHTML = term
          ? `<p class="ovl__none">Aucun résultat pour « ${term.replace(/[<>&]/g, '')} ».</p>`
          : '';
        return;
      }
      list.innerHTML = products
        .map(
          (p) => `<a class="ovl__item" href="${p.url}">
            <span class="ovl__ph">${p.image ? `<img src="${sized(p.image, 120)}" alt="" decoding="async">` : ''}</span>
            <span class="ovl__t">${p.title}</span>
            <span class="price num">${euro(p.priceCents)}</span>
          </a>`
        )
        .join('');
    };

    const query = (term) => {
      if (controller) controller.abort();
      controller = new AbortController();
      list.classList.add('ovl__busy');
      fetch(
        '/search/suggest.json?q=' +
          encodeURIComponent(term) +
          '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last',
        { signal: controller.signal, headers: { Accept: 'application/json' } }
      )
        .then((r) => r.json())
        .then((d) => {
          // La forme de l'API de suggestion varie : on accepte les deux.
          const items = (d.resources.results.products || []).map((p) => ({
            url: p.url,
            title: p.title,
            image:
              (p.featured_image && (p.featured_image.url || p.featured_image)) ||
              p.image ||
              null,
            priceCents: toCents(p.price),
          }));
          render(items, term);
        })
        .catch((err) => {
          if (err.name !== 'AbortError') render([], term);
        })
        .finally(() => list.classList.remove('ovl__busy'));
    };

    input.addEventListener('input', () => {
      const term = input.value.trim();
      clearTimeout(timer);
      if (term.length < 2) {
        render([], '');
        return;
      }
      timer = setTimeout(() => query(term), 180);
    });
  }

  /* ───────────── Barre d'achat collante (fiche produit) ───────────── */

  function stickyBuy() {
    const bar = document.querySelector('[data-stickybuy]');
    const form = document.querySelector('[data-product-form]');
    if (!bar || !form) return;
    const anchor = form.querySelector('[type="submit"]');
    if (!anchor || !('IntersectionObserver' in window)) return;

    // Elle n'apparaît que lorsque le bouton principal a quitté le champ.
    const io = new IntersectionObserver(
      ([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.hidden = !passed;
      },
      { threshold: 0, rootMargin: '0px' }
    );
    io.observe(anchor);

    bar.querySelector('[data-sticky-add]').addEventListener('click', (e) => {
      const id = form.querySelector('[name="id"]').value;
      const qty = form.querySelector('[name="quantity"]');
      addToCart(id, qty ? Number(qty.value) : 1, e.currentTarget);
    });
  }

  /* ──────────────── Recommandations, chargées après coup ──────────────── */

  function recommendations() {
    const host = document.querySelector('[data-recos]');
    if (!host || !host.dataset.url) return;
    const load = () => {
      fetch(host.dataset.url)
        .then((r) => r.text())
        .then((html) => {
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recos]');
          if (fresh && fresh.innerHTML.trim()) host.innerHTML = fresh.innerHTML;
        })
        .catch(() => {});
    };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io.disconnect();
            load();
          }
        },
        { rootMargin: '600px' }
      );
      io.observe(host);
    } else {
      load();
    }
  }



  /* ─────────────── Pas-à-pas de quantité ───────────────
     Le champ nombre natif reste la source de vérité : les boutons ne font que
     l'incrémenter et déclencher un `change`, pour que tout ce qui écoute le
     formulaire continue de fonctionner sans JavaScript dédié. */

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty]');
    if (!btn) return;
    const field = btn.closest('[data-qty-stepper]');
    const input = field && field.querySelector('input');
    if (!input) return;
    e.preventDefault();
    const min = Number(input.min || 0);
    const next = Math.max(min, (Number(input.value) || 0) + Number(btn.dataset.qty));
    if (next === Number(input.value)) return;
    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
    field.querySelectorAll('[data-qty="-1"]').forEach((b) => { b.disabled = next <= min; });
  });

  function syncSteppers() {
    document.querySelectorAll('[data-qty-stepper]').forEach((f) => {
      const input = f.querySelector('input');
      const minus = f.querySelector('[data-qty="-1"]');
      if (input && minus) minus.disabled = Number(input.value) <= Number(input.min || 0);
    });
  }

  /* ─────────────────── Produits récemment vus ───────────────────
     Écrit et lu dans le navigateur du visiteur. Rien ne part au serveur,
     rien n'est conservé ailleurs que sur son appareil. Toute erreur de
     stockage (navigation privée, quota) est avalée : la section disparaît
     simplement, elle ne casse jamais la page. */

  const RV_KEY = 'minouko:vus';
  const RV_MAX = 12;

  function rvRead() {
    try {
      const raw = localStorage.getItem(RV_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (_) {
      return [];
    }
  }

  function rvRemember() {
    const meta = document.getElementById('rv-current');
    if (!meta) return;
    let item;
    try {
      item = JSON.parse(meta.textContent);
    } catch (_) {
      return;
    }
    if (!item || !item.url) return;
    try {
      const list = rvRead().filter((p) => p.url !== item.url);
      list.unshift(item);
      localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, RV_MAX)));
    } catch (_) {
      /* Stockage indisponible : on n'insiste pas. */
    }
  }

  function rvRender() {
    const host = document.querySelector('[data-recently-viewed]');
    if (!host) return;
    const grid = host.querySelector('[data-rv-grid]');
    const current = window.location.pathname;
    const items = rvRead()
      .filter((p) => p.url !== current)
      .slice(0, Number(host.dataset.count || 4));

    if (!items.length) {
      host.hidden = true;
      return;
    }
    grid.innerHTML = items
      .map(
        (p) => `<a class="rv__item" href="${p.url}">
          <span class="rv__ph">${p.img ? `<img src="${sized(p.img, 420)}" alt="" decoding="async">` : ''}</span>
          <span class="rv__t">${p.title}</span>
          <span class="price num">${euro(p.cents)}</span>
        </a>`
      )
      .join('');
    host.hidden = false;

    const clear = host.querySelector('[data-rv-clear]');
    if (clear) {
      clear.addEventListener('click', () => {
        try {
          localStorage.removeItem(RV_KEY);
        } catch (_) {}
        host.hidden = true;
      });
    }
  }

  /* ─────────────────── Tiroir de navigation (mobile) ─────────────────── */

  function navDrawer() {
    const el = document.getElementById('nav-drawer');
    if (!el) return;
    const panel = el.querySelector('.drawer__panel');
    let lastFocus = null;

    const open = () => {
      lastFocus = document.activeElement;
      el.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';
      document.querySelectorAll('[data-nav-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      const x = panel.querySelector('[data-nav-close]');
      if (x) x.focus();
    };
    const close = () => {
      el.removeAttribute('open');
      document.documentElement.style.overflow = '';
      document.querySelectorAll('[data-nav-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (lastFocus) lastFocus.focus();
    };

    document.querySelectorAll('[data-nav-open]').forEach((b) =>
      b.addEventListener('click', (e) => {
        e.preventDefault();
        open();
      })
    );
    el.addEventListener('click', (e) => {
      if (e.target.closest('[data-nav-close]')) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !el.hasAttribute('open')) return;
      close();
    });
  }

  /* ─────────────────────────── Démarrage ─────────────────────────── */

  const boot = () => {
    Drawer.init();
    navDrawer();
    stickyBuy();
    recommendations();
    search();
    rvRemember();
    rvRender();
    syncSteppers();
    gallery();
    variants();
    expanders();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
